import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Brain, Play, Trophy } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabase';

const GrandQuizConfigPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [niveau, setNiveau] = useState('');
  const [matiere, setMatiere] = useState('');
  const [totalQuestions, setTotalQuestions] = useState(20);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultLevel, setDefaultLevel] = useState<string | null>(null);
  const [availableQuestions, setAvailableQuestions] = useState(0);

  const getRegularMatieres = (niveau: string) => {
    const baseRegularMatieres = [
      'Allemand',
      'Anglais',
      'Arts plastiques',
      'Enseignement scientifique',
      'Espagnol',
      'Français',
      'Histoire-Géo-EMC',
      'Musique',
      'Philosophie',
      'Physique-Chimie',
      'Sciences Numériques et Technologie',
      'SVT (Sciences de la Vie et de la Terre)',
      'Technologie'
    ];

    if (!niveau || ['6ème', '5ème', '4ème', '3ème', 'Seconde'].includes(niveau)) {
      baseRegularMatieres.push('Mathématiques');
    }
    if (niveau === 'Seconde') {
      baseRegularMatieres.push('SES (Sciences Économiques et Sociales)');
    }

    return baseRegularMatieres.sort();
  };

  const getSpecialtyMatieres = (niveau: string) => {
    const baseSpecialtyMatieres = [
      'Éduc. Médias et Information (EMI)',
      'Hist. Géo, Géopolitique et Sciences Pol.',
      'Humanités, Littérature et Philosophie',
      'Langues, Littératures et Cultures Étrangères',
      'Numérique et Sciences Informatiques',
      'Sciences de l\'ingénieur'
    ];

    if (['Première', 'Terminale'].includes(niveau)) {
      baseSpecialtyMatieres.push('Mathématiques');
      baseSpecialtyMatieres.push('SES (Sciences Économiques et Sociales)');
    }

    return baseSpecialtyMatieres.sort();
  };

  const optionalMatieres = [
    'Grec',
    'Latin'
  ].sort();

  const niveaux = {
    'Collège': ['6ème', '5ème', '4ème', '3ème'],
    'Lycée': ['Seconde', 'Première', 'Terminale']
  };

  const isSubjectAvailable = (matiere: string, niveau: string): boolean => {
    if (!niveau) return true;

    if (matiere === 'Technologie' && 
        !['6ème', '5ème', '4ème', '3ème'].includes(niveau)) {
      return false;
    }

    if (niveau === '6ème' && ['Latin', 'Grec'].includes(matiere)) {
      return false;
    }

    if (matiere === 'Sciences Numériques et Technologie' && niveau !== 'Seconde') {
      return false;
    }

    if (matiere === 'SES (Sciences Économiques et Sociales)') {
      if (!['Seconde', 'Première', 'Terminale'].includes(niveau)) {
        return false;
      }
      if (['Première', 'Terminale'].includes(niveau)) {
        return getSpecialtyMatieres(niveau).includes(matiere);
      }
      return getRegularMatieres(niveau).includes(matiere);
    }

    if (matiere === 'Mathématiques') {
      if (['Première', 'Terminale'].includes(niveau)) {
        return getSpecialtyMatieres(niveau).includes(matiere);
      }
      return getRegularMatieres(niveau).includes(matiere);
    }

    if (matiere === 'Enseignement scientifique' && 
        !['Première', 'Terminale'].includes(niveau)) {
      return false;
    }

    const specialtyMatieres = [
      'Hist. Géo, Géopolitique et Sciences Pol.',
      'Éduc. Médias et Information (EMI)',
      'Humanités, Littérature et Philosophie',
      'Langues, Littératures et Cultures Étrangères',
      'Numérique et Sciences Informatiques',
      'Sciences de l\'ingénieur'
    ];
    if (specialtyMatieres.includes(matiere) && 
        !['Première', 'Terminale'].includes(niveau)) {
      return false;
    }

    if (matiere === 'Philosophie' && niveau !== 'Terminale') {
      return false;
    }

    return true;
  };

  useEffect(() => {
    const fetchDefaultLevel = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('default_level')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data?.default_level) {
          setDefaultLevel(data.default_level);
          setNiveau(data.default_level);
          
          // Auto-select Quiz global for college levels
          if (['6ème', '5ème', '4ème', '3ème'].includes(data.default_level)) {
            setMatiere('Quiz global');
          }
        }
      } catch (err) {
        console.error('Error fetching default level:', err);
      }
    };

    fetchDefaultLevel();
  }, [user]);

  useEffect(() => {
    const checkAvailableQuestions = async () => {
      if (!niveau || !matiere) {
        setAvailableQuestions(0);
        return;
      }

      try {
        let totalQuestions = 0;

        if (matiere === 'Quiz global') {
          // Pour le quiz global, compter les questions de toutes les matières du Brevet
          const brevetMatieres = ['Français', 'Mathématiques', 'Histoire-Géo-EMC', 'Physique-Chimie', 'SVT (Sciences de la Vie et de la Terre)'];
          
          for (const matiereItem of brevetMatieres) {
            const { data: fiches, error: fichesError } = await supabase
              .from('Fiches')
              .select('id')
              .eq('public', true)
              .eq('niveau', niveau)
              .eq('matiere', matiereItem)
              .eq('quiz', true);

            if (fichesError) continue;

            if (fiches && fiches.length > 0) {
              const ficheIds = fiches.map(f => f.id);

              const { data: quizzes, error: quizzesError } = await supabase
                .from('quiz')
                .select('id')
                .in('fiche_id', ficheIds)
                .eq('is_public', true);

              if (quizzesError) continue;

              if (quizzes && quizzes.length > 0) {
                const quizIds = quizzes.map(q => q.id);

                const { data: questions, error: questionsError } = await supabase
                  .from('questions')
                  .select('id')
                  .in('quiz_id', quizIds);

                if (questionsError) continue;

                if (questions && questions.length > 0) {
                  for (const questionRow of questions) {
                    const { data: questionData, error: questionDetailError } = await supabase
                      .from('questions')
                      .select('*')
                      .eq('id', questionRow.id)
                      .single();

                    if (questionDetailError) continue;

                    for (let i = 1; i <= 10; i++) {
                      if (questionData[`Question ${i}`]) {
                        totalQuestions++;
                      } else {
                        break;
                      }
                    }
                  }
                }
              }
            }
          }
        } else {
          // Logique existante pour une matière spécifique
          const { data: fiches, error: fichesError } = await supabase
            .from('Fiches')
            .select('id')
            .eq('public', true)
            .eq('niveau', niveau)
            .eq('matiere', matiere)
            .eq('quiz', true);

          if (fichesError) throw fichesError;

          if (!fiches || fiches.length === 0) {
            setAvailableQuestions(0);
            return;
          }

          const ficheIds = fiches.map(f => f.id);

          const { data: quizzes, error: quizzesError } = await supabase
            .from('quiz')
            .select('id')
            .in('fiche_id', ficheIds)
            .eq('is_public', true);

          if (quizzesError) throw quizzesError;

          if (!quizzes || quizzes.length === 0) {
            setAvailableQuestions(0);
            return;
          }

          const quizIds = quizzes.map(q => q.id);

          const { data: questions, error: questionsError } = await supabase
            .from('questions')
            .select('id')
            .in('quiz_id', quizIds);

          if (questionsError) throw questionsError;

          if (questions && questions.length > 0) {
            for (const questionRow of questions) {
              const { data: questionData, error: questionDetailError } = await supabase
                .from('questions')
                .select('*')
                .eq('id', questionRow.id)
                .single();

              if (questionDetailError) continue;

              for (let i = 1; i <= 10; i++) {
                if (questionData[`Question ${i}`]) {
                  totalQuestions++;
                } else {
                  break;
                }
              }
            }
          }
        }

        setAvailableQuestions(totalQuestions);
      } catch (err) {
        console.error('Error checking available questions:', err);
        setAvailableQuestions(0);
      }
    };

    checkAvailableQuestions();
  }, [niveau, matiere]);

  const handleNiveauChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newNiveau = e.target.value;
    setNiveau(newNiveau);
    
    // Auto-select Quiz global for college levels
    if (['6ème', '5ème', '4ème', '3ème'].includes(newNiveau)) {
      setMatiere('Quiz global');
    } else {
      setMatiere('');
    }
  };

  const handleStartQuiz = async () => {
    if (!user || !niveau || !matiere) {
      setError('Veuillez sélectionner une classe et une matière');
      return;
    }

    if (availableQuestions < totalQuestions) {
      setError(`Il n'y a que ${availableQuestions} questions disponibles pour cette combinaison. Veuillez choisir un nombre de questions inférieur ou égal.`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create a new grand quiz session
      const { data: session, error: sessionError } = await supabase
        .from('grand_quiz_sessions')
        .insert({
          user_id: user.id,
          niveau,
          matiere,
          total_questions: totalQuestions,
          current_question: 1,
          correct_answers: 0,
          status: 'in_progress'
        })
        .select()
        .single();

      if (sessionError) throw sessionError;

      // Navigate to the quiz page
      navigate(`/grand-quiz/${session.id}`);
    } catch (err) {
      console.error('Error starting grand quiz:', err);
      setError('Erreur lors du démarrage du quiz');
    } finally {
      setIsLoading(false);
    }
  };

  const availableRegularMatieres = getRegularMatieres(niveau).filter(matiere => 
    isSubjectAvailable(matiere, niveau)
  );
  const availableSpecialtyMatieres = getSpecialtyMatieres(niveau).filter(matiere => 
    isSubjectAvailable(matiere, niveau)
  );
  const availableOptionalMatieres = optionalMatieres.filter(matiere => 
    isSubjectAvailable(matiere, niveau)
  );

  // Ajouter "Quiz global" pour les classes de collège
  const getQuizGlobalOptions = () => {
    if (!niveau || !['6ème', '5ème', '4ème', '3ème'].includes(niveau)) {
      return [];
    }
    
    if (niveau === '3ème') {
      return ['Quiz global - Spécial Brevet'];
    }
    
    return ['Quiz global'];
  };

  const quizGlobalOptions = getQuizGlobalOptions();

  // Filtrer les options de questions selon la disponibilité
  const getAvailableQuestionOptions = () => {
    const allOptions = [
      { value: 10, label: '10 questions' },
      { value: 20, label: '20 questions' },
      { value: 50, label: '50 questions' },
      { value: 100, label: '100 questions' }
    ];

    return allOptions.filter(option => availableQuestions >= option.value);
  };

  const availableQuestionOptions = getAvailableQuestionOptions();

  // S'assurer qu'il y a au moins une option disponible et ajuster totalQuestions si nécessaire
  useEffect(() => {
    if (availableQuestionOptions.length > 0) {
      const maxAvailable = Math.max(...availableQuestionOptions.map(opt => opt.value));
      if (totalQuestions > maxAvailable) {
        setTotalQuestions(availableQuestionOptions[0].value);
      }
    }
  }, [availableQuestionOptions, totalQuestions]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-4 py-8 sm:px-6 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Trophy className="h-12 w-12 text-yellow-300 mr-3" />
              <h1 className="text-3xl font-bold text-white">Le Grand Quiz</h1>
            </div>
            <h2 className="text-xl text-purple-100 mb-2">Jusqu'où pourras-tu aller ? Réponds à un maximum de questions pour maximiser ton score, et défie tes potes !</h2>
          </div>
        </div>

        <div className="px-4 py-6 sm:p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Configuration du Quiz</h3>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Configuration fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="niveau" className="block text-sm font-medium text-gray-700 mb-2">
                  Classe
                </label>
                <select
                  id="niveau"
                  value={niveau}
                  onChange={handleNiveauChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                >
                  <option value="">Sélectionnez une classe</option>
                  <optgroup label="Collège">
                    {niveaux.Collège.map((niveau) => (
                      <option key={niveau} value={niveau}>{niveau}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Lycée">
                    {niveaux.Lycée.map((niveau) => (
                      <option key={niveau} value={niveau}>{niveau}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {niveau && (
                <div>
                  <label htmlFor="matiere" className="block text-sm font-medium text-gray-700 mb-2">
                    Matière
                  </label>
                  <select
                    id="matiere"
                    value={matiere}
                    onChange={(e) => setMatiere(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  >
                    <option value="">Sélectionnez une matière</option>
                    {quizGlobalOptions.length > 0 && (
                      <optgroup label="Quiz spéciaux">
                        {quizGlobalOptions.map((option) => (
                          <option key={option} value="Quiz global">{option}</option>
                        ))}
                      </optgroup>
                    )}
                    {availableRegularMatieres.length > 0 && (
                      <optgroup label="Matières générales">
                        {availableRegularMatieres.map((matiere) => (
                          <option key={matiere} value={matiere}>{matiere}</option>
                        ))}
                      </optgroup>
                    )}
                    {availableSpecialtyMatieres.length > 0 && (
                      <optgroup label="Spécialités">
                        {availableSpecialtyMatieres.map((matiere) => (
                          <option key={matiere} value={matiere}>{matiere}</option>
                        ))}
                      </optgroup>
                    )}
                    {availableOptionalMatieres.length > 0 && (
                      <optgroup label="Options">
                        {availableOptionalMatieres.map((matiere) => (
                          <option key={matiere} value={matiere}>{matiere}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              )}

              {matiere && availableQuestionOptions.length > 0 && (
                <div>
                  <label htmlFor="totalQuestions" className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre de questions
                  </label>
                  <select
                    id="totalQuestions"
                    value={totalQuestions}
                    onChange={(e) => setTotalQuestions(Number(e.target.value))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                  >
                    {availableQuestionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Start button */}
            {niveau && matiere && availableQuestionOptions.length > 0 && (
              <div className="text-center">
                <button
                  onClick={handleStartQuiz}
                  disabled={!niveau || !matiere || isLoading || availableQuestions < totalQuestions}
                  className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-lg font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Démarrage...
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5 mr-2" />
                      Lancer le Grand Quiz
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Leaderboard link */}
          <div className="mt-8 text-center border-t pt-6">
            <Link
              to="/grand-quiz/leaderboard"
              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              Voir le classement et les scores
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrandQuizConfigPage;